/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ToolbarButton } from '@ckeditor/ckeditor5-metadata';
import { Box, Tooltip, Typography } from '@mui/material';
import { type ReactNode } from 'react';
import type { ToolbarOptions } from '../../../../types/ToolbarsOptions';
import { mainToolbar, imageToolbar, tableToolbar } from '../../../../constants/toolbars';
import tripleDotSvg from '../../../../assets/IconShowMore.svg?raw';

const imageToolbarItems = imageToolbar.flatMap( items => items.map( item => item.name ) );
const tableToolbarItems = tableToolbar.flatMap( items => items.map( item => item.name ) );

export type UiComponentProps = {
	component: ToolbarButton;
	opacity: number;
	toolbarOptions: ToolbarOptions;
};

const mainIconSize = 14;
const tooltipIconSize = 10;

export function UiComponent( { component, opacity, toolbarOptions }: UiComponentProps ): ReactNode {
	return (
		<Tooltip
			arrow
			disableInteractive
			placement={ 'top-start' }
			title={
				getTitle( {
					component,
					toolbarOptions,
					image: <ToolbarIcon size={ tooltipIconSize } svg={ component.svg }/>
				} )
			}
		>
			<span data-testid={ 'main-icon' }>
				<ToolbarIcon
					size={ mainIconSize }
					opacity={ opacity }
					svg={ component.svg }
				/>
			</span>
		</Tooltip>
	);
}

type getTitleProps = {
	component: ToolbarButton;
	image: ReactNode;
	toolbarOptions: ToolbarOptions;
};

function getTitle( { component, image, toolbarOptions }: getTitleProps ): ReactNode {
	const wrapperProps = {
		style: { margin: 0 },
		'data-testid': 'tooltip-content'
	};

	const toolbarItemDefinition = mainToolbar.flat().find( item => item.name === component.name );

	if ( imageToolbarItems.includes( component.name ) ) {
		return (
			<p { ...wrapperProps }>
				<CustomTypography text={ 'This button appears on the selected image in the editor\'s content.' }/>
			</p>
		);
	}

	if ( tableToolbarItems.includes( component.name ) ) {
		return (
			<p { ...wrapperProps }>
				<CustomTypography text={ 'This button appears on the selected table in the editor\'s content.' }/>
			</p>
		);
	}

	if ( !toolbarItemDefinition ) {
		return (
			<p { ...wrapperProps }>
				<CustomTypography text={ [
					'This button is not used in the current configuration, and its purpose is',
					'fulfilled by other actions or a similar button. It can be later added in the code.'
				].join( ' ' ) }
				/>
			</p>
		);
	}

	if ( toolbarOptions.isMenuBarUsed && toolbarItemDefinition.removeWhenMenuBarOn && component.menuBarPath ) {
		return (
			<p { ...wrapperProps }>
				<CustomTypography text='Based on your current configuration, the '/>
				{ image }
				<CustomTypography text=' button is in the menu bar under '/>
				<b>{ component.menuBarPath }</b>
				<CustomTypography text='.'/>
			</p>
		);
	}

	if ( toolbarOptions.isMenuBarUsed && !toolbarItemDefinition.removeWhenMenuBarOn && component.menuBarPath ) {
		return (
			<p { ...wrapperProps }>
				<CustomTypography text='Based on your current configuration, the '/>
				{ image }
				<CustomTypography text=' button is in the main toolbar'/>
				{ toolbarOptions.isCollapsable && <CollapsibleNote/> }
				<CustomTypography text='. It is also in the menu bar under '/>
				<b>{ component.menuBarPath }</b>
				<CustomTypography text='.'/>
			</p>
		);
	}

	return (
		<p { ...wrapperProps }>
			<CustomTypography text='Based on your current configuration, the '/>
			{ image }
			<CustomTypography text=' button will appear in the main toolbar'/>
			{ toolbarOptions.isCollapsable && <CollapsibleNote/> }
			<CustomTypography text='.'/>
		</p>
	);
}

type ToolbarIconProps = {
	size: number;
	opacity?: number;
	svg: string;
};

function ToolbarIcon( { size, opacity = 1, svg }: ToolbarIconProps ) {
	return (
		<Box
			dangerouslySetInnerHTML={ { __html: normalizeSvgSize( svg ) } }
			component={ 'span' }
			sx={ {
				backgroundColor: 'white',
				display: 'inline-flex',
				justifyContent: 'center',
				border: '1px solid',
				borderColor: '#CCCED1',
				borderRadius: '2px',
				height: `${ size }px`,
				minWidth: `${ size }px`,
				padding: '2px',
				marginRight: '2px',
				opacity,
				'& > svg': {
					// To force correct displaying CKEditor 5 svg in Safari.
					height: '100%'
				}
			} }
		/>
	);
}

function CollapsibleNote(): ReactNode {
	return (
		<span>
			<CustomTypography text=' (it can be wrapped and placed under '/>
			<ToolbarIcon size={ tooltipIconSize } svg={ tripleDotSvg } />
			<CustomTypography text=')'/>
		</span>
	);
}

function CustomTypography( { text }: { text: string } ): ReactNode {
	return (
		<Typography
			component={ 'span' }
			sx={ {
				display: 'inline',
				lineHeight: '18px',
				fontSize: '12px',
				fontWeight: 500
			} }
		>
			{ text }
		</Typography>
	);
}

function normalizeSvgSize( svg: string ): string {
	const heightPattern = /height=["'](\d+)["']/;
	const widthPattern = /width=["'](\d+)["']/;

	return svg
		.replace( heightPattern, '' )
		.replace( widthPattern, '' );
}
